import React, { useCallback, useEffect, useState } from 'react'
import { CSmartTable } from '@coreui/react-pro'
import moment from 'moment'

import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import FixtureService from 'src/service/FixtureService'
import CommonService from 'src/service/CommonService'
import ToastComponent from 'src/components/common/TaostComponent.js'

// Only the filters the listing endpoint actually understands.
const SEARCH_FIELDS = [
  { key: 'team_name', label: 'Team' },
  { key: 'round', label: 'Round' },
  { key: 'match_type', label: 'Match Type' },
]

// A team's name with the code you type into the CSV underneath it.
const TeamCell = ({ name, code }) => (
  <>
    <div className="fw-semibold">{name ?? '-'}</div>
    {code && <code className="small">{code}</code>}
  </>
)

// Read-only lookup table: the fixture IDs and team codes a Squad Import CSV has to quote.
const SquadImportFixture = () => {
  const [loading, setLoading] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState({})
  const [searchField, setSearchField] = useState('team_name')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState({})
  const [gameType, setGameType] = useState()

  useEffect(() => {
    CommonService.getGameType().then((res) => {
      if (res.status === 200) {
        setGameType(res.data)
      }
    })
  }, [])

  const clearSearchKeys = (filter) => {
    const next = { ...filter }
    SEARCH_FIELDS.forEach((f) => delete next[f.key])
    return next
  }

  const applySearch = (value) => {
    setActivePage(1)
    const next = clearSearchKeys(columnFilter)
    if (value) next[searchField] = value
    setColumnFilter(next)
  }

  const handleFieldChange = (key) => {
    setSearchField(key)
    setColumnFilter((prev) => clearSearchKeys(prev))
  }

  const fetchUsers = useCallback(() => {
    const offset = itemsPerPage * activePage - itemsPerPage
    const params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      if (columnFilter[key]) params.append(key, columnFilter[key])
    })
    if (columnSorter && columnSorter.column !== undefined) {
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)
    }

    setLoading(true)
    FixtureService.getFixture(offset, itemsPerPage, activePage, params)
      .then((result) => setUsers(result.data))
      .catch(() => ToastComponent('Unable to load fixtures.', 'error'))
      .finally(() => setLoading(false))
  }, [activePage, itemsPerPage, columnFilter, columnSorter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const isSingleTeam = gameType === 1

  const columns = [
    { label: 'Fixture ID', key: 'id', _style: { width: '10%' } },
    { label: 'Round', key: 'round', _style: { width: '10%' } },
    { label: isSingleTeam ? 'Team' : 'Home Team', key: 'team_name' },
    ...(isSingleTeam ? [] : [{ label: 'Away Team', key: 'team2_name' }]),
    { label: 'Match Type', key: 'match_type', sorter: false },
    { label: 'Start', key: 'start_date', sorter: false, _style: { width: '15%' } },
  ]

  const activeSearchLabel = SEARCH_FIELDS.find((f) => f.key === searchField)?.label

  return (
    <>
      <TableSearchToolbar
        fields={SEARCH_FIELDS}
        field={searchField}
        onFieldChange={handleFieldChange}
        value={columnFilter?.[searchField] || ''}
        onSearch={applySearch}
        placeholder={`Search by ${activeSearchLabel}...`}
      />

      <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
        <CSmartTable
          columns={columns}
          columnSorter={{ external: true }}
          noItemsLabel="No fixtures found."
          scopedColumns={{
            id: (item) => (
              <td>
                <code className="fw-semibold">{item.id}</code>
              </td>
            ),
            round: (item) => <td>{item.round ?? '-'}</td>,
            team_name: (item) => (
              <td>
                <TeamCell name={item.team_name} code={item.team_code} />
              </td>
            ),
            team2_name: (item) => (
              <td>
                <TeamCell name={item.team2_name} code={item.team2_code} />
              </td>
            ),
            match_type: (item) => <td>{item.match_type ?? '-'}</td>,
            start_date: (item) => (
              <td>
                <div>{item.start_date ? moment(item.start_date).format('D.MM.YYYY') : '-'}</div>
                {item.start_time && (
                  <div className="text-medium-emphasis small">
                    {moment(item.start_time, ['HH:mm:ss', 'HH:mm']).format('h:mm A')}
                  </div>
                )}
              </td>
            ),
          }}
          items={users?.data}
          itemsPerPage={itemsPerPage}
          itemsPerPageSelect
          loading={loading}
          pagination={{ external: true }}
          paginationProps={{
            activePage: activePage,
            pages: Math.ceil(users?.total / itemsPerPage) || 1,
          }}
          tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
          onActivePageChange={(page) => setActivePage(page)}
          onItemsPerPageChange={(perPage) => {
            setActivePage(1)
            setItemsPerPage(perPage)
          }}
          onSorterChange={(sorter) => setColumnSorter(sorter)}
        />
      </div>
    </>
  )
}

export default SquadImportFixture
